/**
 * The book owners' form (Coleman, 2026-10-26): the same questions as the old GHL page the book's QR code opened
 * (byanymeanscoaches.com/blueprint), now on the platform at #/blueprint. Name, phone, email, what kind of coach, the
 * levels they coach, the Amazon order ID and when they bought it, and the two boxes, worded as they were.
 *
 * The site is leaving GHL (Coleman, 2026-10-26: "we're moving the bam site right now actually off ghl. and then we run
 * folow ups etc from resend here"), so nothing goes to GHL: owners are kept on the platform (book_claims), emailed
 * from here, and the consent is to emails only. The phone number is asked, but optional.
 *
 * Amazon gives authors no way to look an order up, so the order ID is checked for its shape (111-1234567-1234567)
 * and the owner confirms they bought the book, as before. Pure: the page and api/blueprint.js share it.
 */

export const COACH_TYPES = ["Skills Trainer", "Performance Coach", "Team Coach", "Business Owner"];
export const LEVELS = ["Beginner", "High School", "Prep", "Post-Secondary", "Pro"];
export const ORDER_ID = /^\d{3}-\d{7}-\d{7}$/;
export const BOOK_URL = "https://byanymeanscoaches.com/blueprint-book";

/** The two boxes, as the old form worded them. */
export const CONFIRM_TEXT = "I confirm that I have purchased The Modern Basketball Blueprint by Coleman Ayers. I understand that this resource is exclusively available to verified buyers of the book, and I acknowledge that my access is granted on that basis.";
export const CONSENT_TEXT = "I have read and agreed to the Privacy Policy and Terms & Conditions, and I agree to receive emails from By Any Means Coaches with updates, offers, and important information. Unsubscribe anytime.";

const clip = (s, n) => String(s ?? "").replace(/\s+/g, " ").trim().slice(0, n);
const today = (now = new Date()) => now.toISOString().slice(0, 10);

/** The form, checked: { value } to save, or { error } to show. `email` comes from the signed-in account. */
export function cleanClaim(body = {}, { now = new Date() } = {}) {
  const name = clip(body.name, 80);
  if (!name) return { error: "Add your name." };
  const phone = clip(body.phone, 30);
  if (phone && phone.replace(/\D/g, "").length < 7) return { error: "Check your phone number, or leave it empty." };
  const coachTypes = (Array.isArray(body.coachTypes) ? body.coachTypes : []).filter(x => COACH_TYPES.includes(x));
  if (!coachTypes.length) return { error: "Pick what type of coach you are." };
  const levels = (Array.isArray(body.levels) ? body.levels : []).filter(x => LEVELS.includes(x));
  if (!levels.length) return { error: "Pick the levels of players you work with." };
  const orderId = clip(body.orderId, 40).replace(/\s/g, "");
  if (!ORDER_ID.test(orderId)) return { error: "Check your Amazon order ID: it looks like 111-1234567-1234567." };
  const purchasedOn = clip(body.purchasedOn, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(purchasedOn) || Number.isNaN(Date.parse(purchasedOn)) || purchasedOn > today(now) || purchasedOn < "2024-01-01") return { error: "Add the date you bought the book." };
  if (body.confirm !== true) return { error: "Tick the box to confirm you bought the book." };
  if (body.consent !== true) return { error: "Tick the box to agree to the Privacy Policy and Terms." };
  return { value: { name, phone: phone || null, answers: { coachTypes: [...new Set(coachTypes)], levels: [...new Set(levels)] }, order_id: orderId, purchased_on: purchasedOn } };
}

// The form, kept while they go sign in (or confirm their email in another tab), so it isn't typed twice.
const KEY = "bam_blueprint_claim";
const TTL = 14 * 86400_000;
export function keepClaim(form) {
  try { localStorage.setItem(KEY, JSON.stringify({ form, at: Date.now() })); } catch { /* private window */ }
}
export function clearClaim() {
  try { localStorage.removeItem(KEY); } catch { /* private window */ }
}
export function pendingClaim() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "null");
    return v?.form && Date.now() - v.at < TTL ? v.form : null;
  } catch { return null; }
}

/**
 * The old GHL form's export, as owners to import (Coleman, 2026-10-26: "take all the info from csv and pre-create
 * accounts for them"). Reads any CSV with a header row: the email column is required; name (or first and last name),
 * phone, the Amazon order ID, the purchase date, coach type and levels are taken when there. Rows without a usable
 * email are counted, not guessed. Returns { rows: [{ email, name, phone, order_id, purchased_on, answers }], skipped }.
 */
export function parseOwnersCsv(text = "") {
  const table = [];
  let row = [], cell = "", q = false;
  const src = String(text).replace(/^\uFEFF/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (q) { if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; } else if (ch === '"') q = false; else cell += ch; continue; }
    if (ch === '"') q = true;
    else if (ch === ",") { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && src[i + 1] === "\n") i++; row.push(cell); table.push(row); row = []; cell = ""; }
    else cell += ch;
  }
  if (cell || row.length) { row.push(cell); table.push(row); }
  const rows = table.filter(r => r.some(c => c.trim()));
  if (!rows.length) return { rows: [], skipped: 0 };
  const head = rows[0].map(h => h.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim());
  const col = (...names) => head.findIndex(h => names.some(n => h === n || h.includes(n)));
  const at = { email: col("email"), name: col("full name", "name"), first: col("first name"), last: col("last name"), phone: col("phone"),
    order: col("amazon order", "order id", "order"), date: col("date purchased", "purchase date", "purchased"), type: col("type of coach", "coach type"), level: col("level") };
  if (at.email < 0) return { rows: [], skipped: rows.length - 1, error: "No email column in that file." };
  const get = (r, i) => (i >= 0 ? String(r[i] || "").trim() : "");
  const list = (v) => v.split(/[,;|]/).map(x => x.trim()).filter(Boolean);
  const out = new Map();
  let skipped = 0;
  for (const r of rows.slice(1)) {
    const email = get(r, at.email).toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { skipped++; continue; }
    const name = (at.first >= 0 && get(r, at.first) ? `${get(r, at.first)} ${get(r, at.last)}` : get(r, at.name)).replace(/\s+/g, " ").trim().slice(0, 80) || null;
    const order = get(r, at.order).replace(/\s/g, "");
    const d = get(r, at.date); const iso = d && !Number.isNaN(Date.parse(d)) ? new Date(Date.parse(d)).toISOString().slice(0, 10) : null;
    out.set(email, { email, name, phone: get(r, at.phone).slice(0, 30) || null, order_id: ORDER_ID.test(order) ? order : (order.slice(0, 40) || null), purchased_on: iso,
      answers: { coachTypes: list(get(r, at.type)).filter(x => COACH_TYPES.includes(x)), levels: list(get(r, at.level)).filter(x => LEVELS.includes(x)) } });
  }
  return { rows: [...out.values()], skipped };
}
